import { add } from 'date-fns';
import { produce } from 'immer';
import Cookie from 'js-cookie';
import { type EnrollmentMethod } from '@hungryroot/segment';
import {
  cookieSchema,
  toCanonicalId,
  toCanonicalVariant,
  type ExperimentEnrollments,
} from '../enrollment-shared';
import { COOKIE_NAME } from '../hooks/useClientSidePresignupExperiment';

export { cookieSchema, type ExperimentEnrollments };

export const getPersistedEnrollments = () => {
  const value = Cookie.get(COOKIE_NAME);
  const blank: ExperimentEnrollments = {
    enrollments: [],
  };
  if (value === undefined) {
    return blank;
  }

  try {
    const json = JSON.parse(value);
    return cookieSchema.parse(json);
  } catch {
    return blank;
  }
};

/**
 * All of the visitor's enrollments in canonical form (`experimentId`/`variant`
 * decoded from their stored iteration encoding), deduped to the latest entry
 * per experiment. Reads the same `experiment_enrollments` cookie the rest of
 * the enrollment system uses, so it covers anonymous (client-enrolled) and
 * authenticated (server-synced) visitors alike. Callers that need to forward
 * the full enrollment set off-domain (e.g. to the onboarding agent, which can't
 * read the cookie cross-origin) use this rather than resolving one experiment
 * at a time.
 */
export function getResolvedExperimentEnrollments(): Array<{
  experiment: number;
  variant: number;
}> {
  const byExperiment = new Map<number, number>();
  for (const enrollment of getPersistedEnrollments().enrollments) {
    byExperiment.set(
      toCanonicalId(enrollment.experimentId),
      toCanonicalVariant(enrollment.variant)
    );
  }
  return [...byExperiment].map(([experiment, variant]) => ({
    experiment,
    variant,
  }));
}

export function persistedEnrollment(
  experimentId: number,
  variant: number,
  enrollmentMethod: EnrollmentMethod
) {
  const cookieVariants = getPersistedEnrollments();
  const next = produce(cookieVariants, (draft) => {
    let found: boolean = false;
    draft.enrollments = draft.enrollments.map((d) => {
      if (d.experimentId === experimentId) {
        found = true;
        return {
          experimentId,
          variant,
          enrollment_method: enrollmentMethod,
        };
      }

      return d;
    });

    if (found === false) {
      draft.enrollments.push({
        experimentId,
        variant,
        enrollment_method: enrollmentMethod,
      });
    }
  });

  Cookie.set(COOKIE_NAME, JSON.stringify(next), {
    domain: '.hungryroot.com',
    expires: add(new Date(), {
      years: 1,
    }),
  });
}

export function getPersistedEnrollment(expId: number) {
  const { enrollments } = getPersistedEnrollments();
  return enrollments.find(
    ({ experimentId }) => toCanonicalId(experimentId) === expId
  );
}
